/** The colour meaning of a status, shared by every page. Rendered by StatusBadge and StatusDot. */
export type StatusTone = 'neutral' | 'success' | 'warning' | 'info' | 'danger';

/**
 * What each tone means, so the same kind of status gets the same colour everywhere:
 * - neutral: not started, draft, archived, nothing to do
 * - info: in progress, queued, a type label (Procedure)
 * - warning: needs someone's attention or a decision, partly done, changed
 * - success: done, agreed, live, matched
 * - danger: failed, error, over or under in a way that's wrong
 */
export const STATUS_TONES: readonly StatusTone[] = ['neutral', 'info', 'warning', 'success', 'danger'];

/** A status value's label and tone. Pages keep a `Record<Status, StatusMeta>` for their own statuses. */
export interface StatusMeta {
  label: string;
  tone: StatusTone;
}

const dotClass: Record<StatusTone, string> = {
  neutral: 'bg-muted-foreground/40',
  success: 'bg-success',
  warning: 'bg-warning',
  info: 'bg-info',
  danger: 'bg-danger',
};

const textClass: Record<StatusTone, string> = {
  neutral: 'text-muted-foreground',
  success: 'text-success-strong',
  warning: 'text-warning-strong',
  info: 'text-info-strong',
  danger: 'text-danger-strong',
};

/** Class names for a tone, for the rare place a badge or dot doesn't fit (a coloured count). */
export function statusToneClasses(tone: StatusTone) {
  return { dot: dotClass[tone], text: textClass[tone] };
}
